<script lang="ts">
  import type { GameData, Brand, GearSet, NamedGear } from '../data.js';
  import { i18next } from '../i18n.js';
  import { lang as langState } from '../lang-state.svelte.js';
  import RenderColorText from './RenderColorText.svelte';
  import { findGearTalentBonus } from '../data/gear-talent-bonuses.js';

  interface Props {
    data: GameData;
    availableNamed?: NamedGear[];
    currentBrandId?: string | null;
    currentSetId?: string | null;
    currentNamedId?: string | null;
    onPickBrand: (id: string | null) => void;
    onPickSet: (id: string | null) => void;
    onPickNamed: (id: string | null) => void;
    onClose: () => void;
  }

  let {
    data,
    availableNamed = [],
    currentBrandId = null,
    currentSetId = null,
    currentNamedId = null,
    onPickBrand,
    onPickSet,
    onPickNamed,
    onClose,
  }: Props = $props();

  let lang = $derived(langState.current);
  let query = $state('');
  let filter = $state<'all' | 'brand' | 'set' | 'named' | 'exotic'>('all');

  function focusOnMount(node: HTMLInputElement) {
    queueMicrotask(() => node.focus());
  }

  function tName(id: string, ns: string): string {
    void lang;
    if (!i18next.exists(id, { ns })) return id;
    return i18next.t(id, { ns, defaultValue: id }) as string;
  }
  function bonusesFromI18n(id: string, ns: string): string[] {
    void lang;
    if (!i18next.exists(id, { ns })) return [];
    const r = i18next.t(id, { ns, returnObjects: true }) as unknown;
    if (Array.isArray(r)) return r as string[];
    if (typeof r === 'string' && r.trim()) return [r as string];
    return [];
  }
  const STAT_LABEL: Record<string, { ru: string; en: string }> = {
    wd: { ru: 'Урон от оружия', en: 'Weapon Damage' },
    chc: { ru: 'Шанс крит. попадания', en: 'Crit Chance' },
    chd: { ru: 'Урон крит. попадания', en: 'Crit Damage' },
    hsd: { ru: 'Урон в голову', en: 'Headshot Damage' },
    dta: { ru: 'Урон по броне', en: 'Damage to Armor' },
    dth: { ru: 'Урон по здоровью', en: 'Damage to Health' },
    ooc: { ru: 'Урон вне укрытия', en: 'Out of Cover Damage' },
    armor: { ru: 'Броня', en: 'Armor' },
    health: { ru: 'Здоровье', en: 'Health' },
    skill_tier: { ru: 'Уровень навыков', en: 'Skill Tier' },
    skill_dmg: { ru: 'Урон навыков', en: 'Skill Damage' },
    skill_haste: { ru: 'Ускорение навыков', en: 'Skill Haste' },
    skill_duration: { ru: 'Длительность навыков', en: 'Skill Duration' },
    skill_health: { ru: 'Прочность навыков', en: 'Skill Health' },
    explosive_dmg: { ru: 'Урон взрывов', en: 'Explosive Damage' },
    hazard_prot: { ru: 'Защита от опасностей', en: 'Hazard Protection' },
    hazard_protection: { ru: 'Защита от опасностей', en: 'Hazard Protection' },
    explosive_resist: { ru: 'Защита от взрывов', en: 'Explosive Resistance' },
    explosive_resistance: { ru: 'Защита от взрывов', en: 'Explosive Resistance' },
    shock_resistance: { ru: 'Защита от шока', en: 'Shock Resistance' },
    armor_on_kill: { ru: 'Броня за убийство', en: 'Armor on Kill' },
    health_on_kill: { ru: 'Здоровье за убийство', en: 'Health on Kill' },
    armor_regen: { ru: 'Восстановление брони', en: 'Armor Regen' },
    ammo_cap: { ru: 'Запас патронов', en: 'Ammo Capacity' },
    skill_repair: { ru: 'Восстановление навыков', en: 'Skill Repair' },
    elite: { ru: 'Урон по элитам', en: 'Damage to Elites' },
    handling: { ru: 'Эргономика', en: 'Handling' },
    reload: { ru: 'Скорость перезарядки', en: 'Reload Speed' },
    rof: { ru: 'Скорострельность', en: 'Rate of Fire' },
    rpm: { ru: 'Скорострельность', en: 'Rate of Fire' },
    range: { ru: 'Оптимальная дальность', en: 'Optimal Range' },
    mag: { ru: 'Размер магазина', en: 'Mag Size' },
    stability: { ru: 'Стабильность', en: 'Stability' },
    accuracy: { ru: 'Точность', en: 'Accuracy' },
    swap: { ru: 'Скорость смены оружия', en: 'Swap Speed' },
    status_effects: { ru: 'Эффекты состояния', en: 'Status Effects' },
    ar_dmg: { ru: 'Урон штурмовых винтовок', en: 'AR Damage' },
    smg_dmg: { ru: 'Урон пистолетов-пулемётов', en: 'SMG Damage' },
    lmg_dmg: { ru: 'Урон пулемётов', en: 'LMG Damage' },
    mmr_dmg: { ru: 'Урон снайперских винтовок', en: 'MMR Damage' },
    rifle_dmg: { ru: 'Урон винтовок', en: 'Rifle Damage' },
    shotgun_dmg: { ru: 'Урон дробовиков', en: 'Shotgun Damage' },
    pistol_dmg: { ru: 'Урон пистолетов', en: 'Pistol Damage' },
  };
  function statName(s: string): string {
    return STAT_LABEL[s]?.[lang === 'ru' ? 'ru' : 'en'] ?? s;
  }
  function fmtBonus(bonus: { stat: string; value: number; isPct?: boolean }): string {
    const sign = bonus.value >= 0 ? '+' : '';
    const unit = bonus.isPct ? '%' : '';
    return `${sign}${bonus.value}${unit} ${statName(bonus.stat)}`;
  }
  function brandBonusLines(b: Brand): string[] {
    return brandBonusLinesGrouped(b);
  }
  function brandBonusLinesGrouped(b: Brand): string[] {
    const byPcs = new Map<number, string[]>();
    for (const x of b.bonuses ?? []) {
      const arr = byPcs.get(x.pieces) ?? [];
      arr.push(fmtBonus(x.bonus));
      byPcs.set(x.pieces, arr);
    }
    const sorted = [...byPcs.entries()].sort((a, b) => a[0] - b[0]);
    return sorted.map(([pcs, parts]) => `[${pcs} ч.] ${parts.join(' · ')}`);
  }
  function setBonusLines(s: GearSet): string[] {
    const fromI18n = bonusesFromI18n(s.id, 'set-bonuses');
    if (fromI18n.length) return fromI18n;
    return (s.numericBonuses ?? []).map((x) => `[${x.pieces} ч.] ${fmtBonus(x.bonus)}`);
  }
  /** Group set bonuses by pieces count → one line per tier "[N ч.] +X Stat · +Y Stat". */
  function setBonusLinesGrouped(s: GearSet): string[] {
    const byPcs = new Map<number, string[]>();
    for (const x of s.numericBonuses ?? []) {
      const arr = byPcs.get(x.pieces) ?? [];
      arr.push(fmtBonus(x.bonus));
      byPcs.set(x.pieces, arr);
    }
    const sorted = [...byPcs.entries()].sort((a, b) => a[0] - b[0]);
    return sorted.map(([pcs, parts]) => `[${pcs} ч.] ${parts.join(' · ')}`);
  }
  function namedBonus(id: string): string {
    void lang;
    if (!i18next.exists(id, { ns: 'named-bonus' })) return '';
    return i18next.t(id, { ns: 'named-bonus', defaultValue: '' }) as string;
  }

  function talentLine(talentId: string | null | undefined): { name: string; bonus: string } {
    if (!talentId) return { name: '', bonus: '' };
    const tb = findGearTalentBonus(`gt_${talentId}`);
    const localName = i18next.exists(talentId, { ns: 'talents' })
      ? (i18next.t(talentId, { ns: 'talents' }) as string)
      : (tb?.name ?? talentId);
    let bonus = '';
    if (tb && tb.value && tb.stat && tb.bucket !== 'special') {
      const sign = tb.bonusType === 'amplified' ? '×' : '+';
      bonus = `${sign}${tb.value}% ${statName(tb.stat)}`;
    }
    return { name: localName, bonus };
  }

  type UnifiedItem =
    | { kind: 'brand'; id: string; name: string; core?: string; bonuses: string[] }
    | { kind: 'set'; id: string; name: string; type?: string; bonuses: string[]; chestTalent: { name: string; bonus: string }; backpackTalent: { name: string; bonus: string } }
    | { kind: 'named'; id: string; name: string; brand?: string; bonus: string; isExotic?: boolean };

  let items = $derived.by<UnifiedItem[]>(() => {
    void lang;
    const q = query.trim().toLowerCase();
    const all: UnifiedItem[] = [];

    if (filter === 'all' || filter === 'brand') {
      for (const b of data.brands as Brand[]) {
        const name = tName(b.id, 'brands');
        all.push({ kind: 'brand', id: b.id, name, core: b.core, bonuses: brandBonusLines(b) });
      }
    }
    if (filter === 'all' || filter === 'set') {
      for (const s of data.sets as GearSet[]) {
        if (s.type === 'brand_set') continue;
        const name = tName(s.id, 'gear-sets');
        all.push({
          kind: 'set',
          id: s.id,
          name,
          type: s.type,
          bonuses: setBonusLinesGrouped(s),
          chestTalent: talentLine(s.chestTalentId),
          backpackTalent: talentLine(s.backpackTalentId),
        });
      }
    }
    if (filter === 'all' || filter === 'named' || filter === 'exotic') {
      for (const n of availableNamed) {
        const isExo = !!n.isExotic;
        if (filter === 'named' && isExo) continue;
        if (filter === 'exotic' && !isExo) continue;
        const name = tName(n.id, 'named-gear');
        all.push({ kind: 'named', id: n.id, name, brand: n.brand, bonus: namedBonus(n.id), isExotic: isExo });
      }
    }

    return all
      .filter((x) => {
        if (!q) return true;
        if (x.name.toLowerCase().includes(q) || x.id.toLowerCase().includes(q)) return true;
        if (x.kind === 'brand' || x.kind === 'set') {
          if ((x as { bonuses: string[] }).bonuses.some((b) => b.toLowerCase().includes(q))) return true;
        }
        if (x.kind === 'set') {
          const s = x as { chestTalent: { name: string }; backpackTalent: { name: string } };
          if (s.chestTalent.name.toLowerCase().includes(q) || s.backpackTalent.name.toLowerCase().includes(q)) return true;
        }
        if (x.kind === 'named') {
          if ((x as { bonus?: string }).bonus?.toLowerCase().includes(q)) return true;
        }
        return false;
      })
      .sort((a, b) => {
        const order: Record<string, number> = { brand: 0, set: 1, named: 2 };
        const oa = order[a.kind], ob = order[b.kind];
        if (oa !== ob) return oa - ob;
        return a.name.localeCompare(b.name);
      });
  });

  function isCurrent(it: UnifiedItem): boolean {
    if (it.kind === 'brand') return currentBrandId === it.id;
    if (it.kind === 'set') return currentSetId === it.id;
    if (it.kind === 'named') return currentNamedId === it.id;
    return false;
  }

  function pick(it: UnifiedItem) {
    if (it.kind === 'brand') onPickBrand(it.id);
    else if (it.kind === 'set') onPickSet(it.id);
    else onPickNamed(it.id);
    onClose();
  }

  function clearAll() {
    onPickBrand(null);
    onPickSet(null);
    onPickNamed(null);
    onClose();
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') onClose();
  }

  let counts = $derived.by(() => {
    void lang;
    const exoCount = availableNamed.filter((n) => n.isExotic).length;
    const realSets = data.sets.filter((s) => s.type !== 'brand_set').length;
    return {
      brand: data.brands.length,
      set: realSets,
      named: availableNamed.length - exoCount,
      exotic: exoCount,
    };
  });

</script>

<svelte:window on:keydown={onKey} />

<div class="gp-overlay" onclick={onClose} role="presentation"></div>
<div class="gp-modal">
  <div class="gp-title">
    <span>{lang === 'ru' ? 'Выбрать экипировку' : 'Pick gear'}</span>
    <button class="gp-close" onclick={onClose}>×</button>
  </div>

  <div class="gp-toolbar">
    <input
      class="input"
      type="search"
      placeholder={lang === 'ru' ? 'Поиск по всем брендам, сетам, именным...' : 'Search across all brands, sets, named...'}
      bind:value={query}
      use:focusOnMount
    />
    <div class="chip-filters">
      <button class="chip" class:on={filter === 'all'} onclick={() => (filter = 'all')}>
        {lang === 'ru' ? 'Все' : 'All'} · {counts.brand + counts.set + counts.named}
      </button>
      <button class="chip brand" class:on={filter === 'brand'} onclick={() => (filter = 'brand')}>
        {lang === 'ru' ? 'Бренды' : 'Brands'} · {counts.brand}
      </button>
      <button class="chip set" class:on={filter === 'set'} onclick={() => (filter = 'set')}>
        {lang === 'ru' ? 'Сеты' : 'Sets'} · {counts.set}
      </button>
      {#if counts.named > 0}
        <button class="chip named" class:on={filter === 'named'} onclick={() => (filter = 'named')}>
          {lang === 'ru' ? 'Именные' : 'Named'} · {counts.named}
        </button>
      {/if}
      {#if counts.exotic > 0}
        <button class="chip exotic" class:on={filter === 'exotic'} onclick={() => (filter = 'exotic')}>
          {lang === 'ru' ? 'Экзотик' : 'Exotic'} · {counts.exotic}
        </button>
      {/if}
    </div>
  </div>

  <div class="gp-grid">
    <button class="gp-card gp-clear" onclick={clearAll}>
      <div class="gp-name">— {lang === 'ru' ? 'Очистить' : 'Clear'} —</div>
    </button>
    {#each items as it (it.kind + ':' + it.id)}
      <button
        class="gp-card"
        class:current={isCurrent(it)}
        data-type={it.kind === 'set' ? (it as { type?: string }).type : ''}
        data-kind={it.kind}
        data-exotic={it.kind === 'named' && (it as { isExotic?: boolean }).isExotic ? 'true' : 'false'}
        onclick={() => pick(it)}
      >
        <div class="gp-head">
          <span class="gp-name">{it.name}</span>
        </div>
        {#if it.kind === 'brand' || it.kind === 'set'}
          <div class="gp-bonus-block">
            {#each (it as { bonuses: string[] }).bonuses as ln, i (i)}
              {@const m = ln.match(/^\[(\d+)\s*ч\.\]\s*(.*)$/)}
              <div class="gp-bonus-row">
                {#if m}<span class="pcs-pill">{m[1]} ч.</span><span class="bonus-txt"><RenderColorText text={m[2]} /></span>{:else}<span class="bonus-txt"><RenderColorText text={ln} /></span>{/if}
              </div>
            {/each}
          </div>
          {#if it.kind === 'set'}
            {@const s = it as { chestTalent: { name: string; bonus: string }; backpackTalent: { name: string; bonus: string } }}
            {#if s.chestTalent.name || s.backpackTalent.name}
              <div class="gp-talents">
                {#if s.chestTalent.name}
                  <div class="gp-talent">
                    <span class="t-slot">🎯 {lang === 'ru' ? 'Жилет' : 'Chest'}</span>
                    <span class="t-name">{s.chestTalent.name}</span>
                    {#if s.chestTalent.bonus}<span class="t-bonus">{s.chestTalent.bonus}</span>{/if}
                  </div>
                {/if}
                {#if s.backpackTalent.name}
                  <div class="gp-talent">
                    <span class="t-slot">🎒 {lang === 'ru' ? 'Рюкзак' : 'Backpack'}</span>
                    <span class="t-name">{s.backpackTalent.name}</span>
                    {#if s.backpackTalent.bonus}<span class="t-bonus">{s.backpackTalent.bonus}</span>{/if}
                  </div>
                {/if}
              </div>
            {/if}
          {/if}
        {:else if it.kind === 'named'}
          {#if (it as { bonus?: string }).bonus}<div class="gp-bonus gp-locked">🔒 <RenderColorText text={(it as { bonus?: string }).bonus} /></div>{/if}
        {/if}
      </button>
    {/each}
    {#if items.length === 0}
      <div class="gp-empty">{lang === 'ru' ? 'Ничего не найдено' : 'No matches'}</div>
    {/if}
  </div>
</div>

<style>
  .gp-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.7); z-index: 1000; }
  .gp-modal {
    position: fixed; inset: 40px auto auto 50%; transform: translateX(-50%);
    width: min(900px, calc(100vw - 40px));
    height: calc(100vh - 80px);
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: var(--r);
    z-index: 1001;
    padding: 14px;
    display: flex; flex-direction: column; gap: 10px;
    box-shadow: 0 24px 60px rgba(0,0,0,.8);
  }
  .gp-title {
    display: flex; align-items: center; justify-content: space-between;
    font: 700 13px/1 var(--f-display); letter-spacing: .14em;
    color: var(--orange); text-transform: uppercase;
    padding-bottom: 8px; border-bottom: 1px solid var(--border);
  }
  .gp-close {
    background: transparent; border: none; color: var(--text);
    font-size: 20px; cursor: pointer; padding: 2px 10px;
  }
  .gp-close:hover { color: var(--red); }
  .gp-toolbar { display: flex; flex-direction: column; gap: 6px; }
  .gp-toolbar .input { padding: 8px 12px; font-size: 13px; }
  .chip-filters { display: flex; gap: 4px; flex-wrap: wrap; }
  .chip {
    padding: 5px 10px;
    background: var(--bg-2); border: 1px solid var(--border);
    color: var(--muted); border-radius: 999px; cursor: pointer;
    font: 700 10px/1 var(--f-display); letter-spacing: .1em;
    text-transform: uppercase; transition: all .12s;
  }
  .chip:hover { border-color: var(--border-hi); color: var(--text-dim); }
  .chip.on { background: var(--orange); color: #000; border-color: var(--orange); }
  .chip.brand.on { background: var(--blue); border-color: var(--blue); }
  .chip.set.on { background: var(--green); border-color: var(--green); }
  .chip.named.on { background: var(--named); border-color: var(--named); }
  .chip.exotic.on { background: var(--exotic); border-color: var(--exotic); color: #000; }

  .gp-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 8px; overflow-y: auto;
    padding-right: 4px;
  }
  .gp-card {
    position: relative;
    background: var(--bg-2);
    border: 1px solid var(--border);
    border-radius: var(--r);
    padding: 12px 14px;
    text-align: left;
    cursor: pointer;
    transition: border-color .15s, background .15s, transform .12s;
    display: flex; flex-direction: column; gap: 6px;
  }
  .gp-card:hover {
    border-color: var(--border-hi); background: var(--card-2);
    transform: translateY(-1px);
  }
  .gp-card.current { border-color: var(--orange); background: rgba(254,175,16,.08); box-shadow: 0 0 0 1px rgba(254,175,16,.4) inset; }
  .gp-card[data-kind="brand"] { border-left: 3px solid var(--blue); }
  .gp-card[data-kind="set"] {
    border-left: 3px solid var(--green);
    background: linear-gradient(180deg, rgba(61,166,120,.10) 0%, rgba(61,166,120,.04) 100%);
  }
  .gp-card[data-kind="set"]:hover { background: linear-gradient(180deg, rgba(61,166,120,.16) 0%, rgba(61,166,120,.08) 100%); }
  .gp-card[data-kind="named"] { border-left: 3px solid var(--named); }
  .gp-card[data-exotic="true"] { border-left: 3px solid var(--exotic) !important; }
  .gp-card.gp-clear { grid-column: 1 / -1; text-align: center; color: var(--muted); padding: 8px; }
  .gp-head { display: flex; align-items: center; gap: 6px; padding-bottom: 4px; border-bottom: 1px solid rgba(255,255,255,.05); }
  .gp-name { font-weight: 700; font-size: 13px; color: var(--text); flex: 1; letter-spacing: .01em; }
  .gp-bonus { font-size: 11px; color: var(--text-dim); }
  .gp-bonus.gp-locked { color: var(--named); font-style: italic; }
  .gp-bonus-block { display: flex; flex-direction: column; gap: 4px; margin-top: 2px; }
  .gp-bonus-row {
    display: flex; align-items: flex-start; gap: 8px;
    font-size: 11.5px; line-height: 1.35; color: var(--text);
  }
  .pcs-pill {
    flex: 0 0 auto;
    font: 700 9px/1 var(--f-mono); letter-spacing: .05em;
    padding: 3px 7px; border-radius: 999px;
    background: linear-gradient(180deg, rgba(89,178,236,.22), rgba(89,178,236,.10));
    color: var(--blue);
    border: 1px solid rgba(89,178,236,.28);
    min-width: 36px; text-align: center;
    margin-top: 1px;
  }
  .gp-card[data-kind="set"] .pcs-pill {
    background: linear-gradient(180deg, rgba(61,166,120,.30), rgba(61,166,120,.14));
    color: var(--green);
    border-color: rgba(61,166,120,.35);
  }
  .bonus-txt { color: var(--text); }
  .gp-talents {
    display: flex; flex-direction: column; gap: 4px;
    margin-top: 6px; padding-top: 6px;
    border-top: 1px dashed rgba(61,166,120,.25);
  }
  .gp-talent {
    display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap;
    font-size: 11px;
  }
  .gp-talent .t-slot {
    font: 700 9px/1 var(--f-display); letter-spacing: .08em;
    text-transform: uppercase; color: var(--muted);
    flex: 0 0 auto;
  }
  .gp-talent .t-name { color: var(--green); font-weight: 600; }
  .gp-talent .t-bonus {
    margin-left: auto;
    font: 700 9px/1 var(--f-mono);
    color: var(--orange);
    background: rgba(254,175,16,.10);
    padding: 2px 6px; border-radius: 3px;
  }
  .gp-empty { grid-column: 1 / -1; padding: 40px; text-align: center; color: var(--muted); font-style: italic; }
</style>
